import { _, helpers, mui, React, ts, ui } from '_core';

import { prepareDistributionData } from './helpers';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  hasSPPipeline: boolean;
  hasPipeline: boolean;
  fullScreen: boolean;
  title: string;
};

type TabTypes = 'signal_' | 'pipeline_' | 'sp_pipeline_';

const Chart = ({ id, readOnly, loadWidgetData, file, hasSPPipeline, hasPipeline, fullScreen, title }: ChartProps) => {
  let tabTypes = ['signal_'] as TabTypes[];
  if (hasPipeline) tabTypes = ['pipeline_'].concat(tabTypes) as TabTypes[];
  if (hasSPPipeline) tabTypes = ['sp_pipeline_'].concat(tabTypes) as TabTypes[];

  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [formattedData, setFormattedData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);
  const [tab, setTab] = React.useState<TabTypes>(tabTypes.at(0));

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
          rename: { build_date: 'date' },
        },
      });
      if (response) setData(response.data);
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  React.useEffect(() => {
    if (!_.isNil(data)) {
      const filteredData = data.map((item) => {
        const filteredColumns = ['date', ..._.filter(Object.keys(item), (key) => key.startsWith(tab))];
        return _.pick(item, filteredColumns);
      });

      setFormattedData(prepareDistributionData(filteredData, tab));
    }
  }, [data, tab]);

  const getMainTabTitle = (tab: TabTypes) => {
    if (tab == 'signal_') return 'Raw';
    if (tab == 'pipeline_') return 'After Pipeline';
    return 'After Portfolio Pipeline';
  };

  const getTabs = () => (
    <>
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={tab}
        onChange={(_e, newTab) => setTab(newTab as TabTypes)}
      >
        {tabTypes.map((f) => (
          <mui.core.Tab key={f} label={getMainTabTitle(f)} value={f} />
        ))}
      </mui.core.Tabs>
    </>
  );

  return (
    <mui.core.Box display="flex" flexDirection="column" height="100%">
      {getTabs()}
      <mui.core.Box flexGrow={1}>
        <ui.LineChart
          id={id}
          data={formattedData}
          yAxisLabel="Exposures"
          fullScreen={fullScreen}
          readOnly={readOnly}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          exportTitle={title}
        />
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
